import { aggregateWorkoutMetrics } from "../utils/workoutMetrics";

/* Presentation adapter for the weekly-plan detail read model.
 *
 * `/program/all/:programId` and `/program/cycles/:cycleId` are DIFFERENT domain
 * models and stay that way. What they share is the read-only presentation of a
 * workout, so this module reshapes the already-fetched weekly-plan detail read
 * model into the shape the Phase 1 shared product cards already consume. It
 * fetches nothing, computes no metrics and invents no cycle semantics. */

// Canonical muscle taxonomy (key, label and order) read from the frozen metrics
// seam without recomputing anything: aggregating zero workouts returns exactly
// the canonical rows with `rawSets: 0`.
const CANONICAL_MUSCLE_ROWS = aggregateWorkoutMetrics([]).muscleDistribution;

export function toMuscleDistributionItems(weeklyTotals) {
  const totals = weeklyTotals || {};

  return CANONICAL_MUSCLE_ROWS.map((row) => ({
    key: row.key,
    label: row.label,
    rawSets: Number(totals[row.key]) || 0,
  }));
}

/* The weekly-plan detail endpoint serialises an already-aggregated reps label
 * (`8`, `8-12`, `30s`, `30-45s`, or `Custom reps` when no usable rep value
 * exists). This maps that label back onto the canonical additive display shape
 * without fabricating per-set values it does not have. An unparseable label
 * yields no prescription rather than invented copy. */
export function toRepDisplay(repsLabel) {
  const label = String(repsLabel ?? "").trim();

  if (!label) {
    return null;
  }

  const secondsRange = label.match(/^(\d+)-(\d+)s$/);
  if (secondsRange) {
    // The aggregate min-max is the only truthful value available; it is carried
    // verbatim through the seconds display field.
    return { repMode: "seconds", targetSeconds: `${secondsRange[1]}-${secondsRange[2]}` };
  }

  const seconds = label.match(/^(\d+)s$/);
  if (seconds) {
    return { repMode: "seconds", targetSeconds: Number(seconds[1]) };
  }

  const repRange = label.match(/^(\d+)-(\d+)$/);
  if (repRange) {
    return {
      repMode: "repRange",
      minReps: Number(repRange[1]),
      maxReps: Number(repRange[2]),
    };
  }

  const reps = label.match(/^(\d+)$/);
  if (reps) {
    return { repMode: "reps", targetReps: Number(reps[1]) };
  }

  return null;
}

function toSets(idPrefix, prescription) {
  const setCount = Number(prescription?.setCount);
  const resolvedCount = Number.isFinite(setCount) && setCount > 0 ? setCount : 0;
  const display = toRepDisplay(prescription?.repsLabel);
  const rirBySet = Array.isArray(prescription?.rirBySet) ? prescription.rirBySet : [];

  return Array.from({ length: resolvedCount }, (_, index) => {
    const set = { id: `${idPrefix}-set-${index + 1}` };
    const rir = rirBySet[index];
    const hasRir = rir !== null && rir !== undefined && Number.isFinite(Number(rir));

    if (display || hasRir) {
      // The stored per-set RIR is shown as is; an absent RIR adds no intensity.
      set.display = {
        ...(display || {}),
        ...(hasRir ? { intensity: { mode: "rir", value: Number(rir) } } : {}),
      };
    }

    return set;
  });
}

export function toViewerBlock(block) {
  if (!block) {
    return null;
  }

  if (block.type === "cardio") {
    return {
      id: block.id,
      type: "cardio",
      exerciseId: block.exercise?.exerciseId || null,
      exercise: block.exercise?.name || "Cardio",
      // The weekly-plan detail endpoint returns cardio under `prescription`.
      cardioPrescription: block.prescription || block.cardioPrescription || null,
      notes: block.notes || null,
    };
  }

  if (block.type === "single") {
    return {
      id: block.id,
      type: "single",
      exerciseId: block.exercise?.exerciseId || null,
      exercise: block.exercise?.name || "Unnamed exercise",
      sets: toSets(block.id, block.prescription),
      tempo: block.prescription?.tempoLabel || null,
      rest: block.prescription?.restLabel || null,
      notes: block.notes || null,
    };
  }

  if (block.type === "superset") {
    const exercises = (block.exercises || []).map((exercise, index) => ({
      id: `${block.id}-lane-${index + 1}`,
      label: exercise.laneLabel,
      exerciseId: exercise.exerciseId || null,
      exercise: exercise.name || "Unnamed exercise",
      sets: toSets(`${block.id}-lane-${index + 1}`, exercise.prescription),
      tempo: exercise.prescription?.tempoLabel || null,
    }));

    return {
      id: block.id,
      type: "superset",
      // Rounds are read from the first lane's own set count. The weekly-plan
      // read model carries no separate round count; this mirrors the existing
      // per-lane set presentation and adds no new domain concept.
      sets: exercises[0]?.sets.length ?? 0,
      rest: block.restLabel || null,
      notes: block.notes || null,
      exercises,
    };
  }

  return { id: block.id, type: block.type || "unsupported" };
}
